import { createSelector } from "reselect";

const getPrivilegesState = state => state.ptzPrivileges;
const getCameraIdProp = (state, props) => props.cameraId;

export const getCanControlPTZ = createSelector([getPrivilegesState, getCameraIdProp], (privileges, cameraId) => {
	const privilegeState = privileges?.[cameraId];

	if (!privilegeState) return false;

	if (privilegeState.isLockedByCurrentUser && privilegeState.canControlPTZ) return true;

	if (privilegeState.canControlPTZ && privilegeState?.lock === null) return true;
	return false;
});

export const getPtzLockType = createSelector(
	[state => state.ptzPrivileges, (state, props) => props.cameraId],
	(privileges, cameraId) => {
		const privilegeState = privileges?.[cameraId];

		// No state for this camera yet
		if (!privilegeState) {
			return null;
		}

		// Only when canControlPTZ === false we care about the lock
		return privilegeState.lock?.type || null; // "explicit" | "temporary" | null
	}
);

export const getPtzLockedOwner = createSelector(
	[state => state.ptzPrivileges, (state, props) => props.cameraId],
	(privileges, cameraId) => {
		const privilegeState = privileges?.[cameraId];

		// No privilege state yet → nothing to show
		if (!privilegeState) {
			return null;
		}

		// When user cannot control → return lock owner name (if present)
		return privilegeState.lock?.ownerDisplayName || null;
	}
);

export const canAccessPtzLockMenu = createSelector([getPrivilegesState, getCameraIdProp], (privileges, cameraId) => {
	const privilegeState = privileges?.[cameraId];
	if (!privilegeState) return false;

	const { lock, viewer, isSharedFeed } = privilegeState;

	//If the feed is shared, only the owning organization should have a higher privilege when the camera is locked.
	if (isSharedFeed) return false;

	// No privilege context → safest to hide
	if (!viewer) return false;

	// No lock => user can always issue an explicit lock
	if (!lock) {
		return true;
	}

	// If current user owns the lock, they should always see the menu (so they can unlock)
	if (lock.ownerUserId && lock.ownerUserId === viewer.userId) {
		return true;
	}

	// Temporary lock held by someone else:
	// per spec, explicit lock can override temp regardless of privilege
	if (lock.type === "temporary") {
		return true;
	}

	// Explicit lock held by someone else:
	// require viewerPrivilege >= ownerPrivilege
	const viewerPrivilege = typeof viewer.privilege === "number" ? viewer.privilege : null;
	const ownerPrivilege = lock && typeof lock.ownerPrivilege === "number" ? lock.ownerPrivilege : null;

	// If we can't calculate, be conservative and hide
	if (viewerPrivilege == null || ownerPrivilege == null) {
		return false;
	}

	return viewerPrivilege >= ownerPrivilege;
});

export const getIsCameraExplicitlyLocked = createSelector(
	[getPrivilegesState, getCameraIdProp],
	(privileges, cameraId) => {
		const privilegeState = privileges?.[cameraId];
		if (!privilegeState || !privilegeState.lock) {
			return false;
		}

		// lock.type is "explicit" | "temporary"
		return privilegeState.lock.type === "explicit";
	}
);

export const isSharedFeed = createSelector([getPrivilegesState, getCameraIdProp], (privileges, cameraId) => {
	const privilegeState = privileges?.[cameraId];
	if (!privilegeState) return false;

	return privilegeState.isSharedFeed;
});
